import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
  Menu,
  MenuItem,
  alpha,
} from '@mui/material';
import { AdminPanelSettings, Air, Backpack, School, Menu as MenuIcon, CalendarMonth, Explore, People, DynamicFeed, GroupsRounded, Diversity3 as ClubIcon, ArrowDropDown, PersonSearch, Public as PublicIcon } from '@mui/icons-material';
import BrandLogo from './BrandLogo';
import NereusIcon from './NereusIcon';
import { authService } from '../services/authService';

type NavItem = { label: string; icon: React.ReactElement; path: string };
type NavGroup = { label: string; icon: React.ReactElement; items: NavItem[] };

const PEOPLE_GROUP: NavGroup = {
  label: 'People',
  icon: <People />,
  items: [
    { label: 'Buddies', icon: <People fontSize="small" />, path: '/buddies' },
    { label: 'Discover', icon: <PersonSearch fontSize="small" />, path: '/discover-divers' },
    { label: 'Communities', icon: <PublicIcon fontSize="small" />, path: '/communities' },
    { label: 'Groups', icon: <GroupsRounded fontSize="small" />, path: '/groups' },
    { label: 'Clubs', icon: <ClubIcon fontSize="small" />, path: '/clubs' },
  ],
};

const FEED_ITEM: NavItem = { label: 'Feed', icon: <DynamicFeed />, path: '/feed' };

const AppNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => authService.getUser());
  const [menuAnchor, setMenuAnchor] = useState<null | { label: string; el: HTMLElement }>(null);

  useEffect(() => {
    return authService.subscribe(() => setCurrentUser(authService.getUser()));
  }, []);

  // The logbook, planning and training tools are still here, just grouped under "My Diving"
  // so the community (Feed, People) leads the navigation.
  // Gated features are hidden unless an admin has granted access, so nobody lands on a 403.
  const myDivingGroup: NavGroup = {
    label: 'My Diving',
    icon: <Explore />,
    items: [
      { label: 'Calendar', icon: <CalendarMonth fontSize="small" />, path: '/calendar' },
      { label: 'Dive Log & Sites', icon: <Explore fontSize="small" />, path: '/explore' },
      { label: 'Equipment', icon: <Backpack fontSize="small" />, path: '/equipment' },
      ...(currentUser?.hasFlashcardAccess ? [{ label: 'Flashcards', icon: <School fontSize="small" />, path: '/flashcards' }] : []),
      ...(currentUser?.hasApneaAccess ? [{ label: 'Apnea', icon: <Air fontSize="small" />, path: '/apnea' }] : []),
    ],
  };
  const groups: NavGroup[] = [PEOPLE_GROUP, myDivingGroup];

  const topLevelItems: NavItem[] = [
    FEED_ITEM,
    ...(currentUser?.hasChatAccess ? [{ label: 'Nereus', icon: <NereusIcon />, path: '/chat' }] : []),
    ...(currentUser?.isAdmin ? [{ label: 'Admin', icon: <AdminPanelSettings />, path: '/admin' }] : []),
  ];

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  // Determine the active path. Only ever returns a path that is actually rendered — a gated
  // item can be hidden while its route is still briefly mounted.
  const getActivePath = (): string => {
    const p = location.pathname;
    let active = '/feed';
    if (p.startsWith('/calendar') || p.startsWith('/trips')) active = '/calendar';
    else if (p === '/explore' || p.startsWith('/dive/') || p.startsWith('/dive-site') || p.startsWith('/dive-sites')) active = '/explore';
    else if (p === '/buddies') active = '/buddies';
    else if (p === '/discover-divers') active = '/discover-divers';
    else if (p.startsWith('/communities')) active = '/communities';
    else if (p.startsWith('/groups')) active = '/groups';
    else if (p.startsWith('/clubs')) active = '/clubs';
    else if (p === '/equipment') active = '/equipment';
    else if (p === '/flashcards') active = '/flashcards';
    else if (p.startsWith('/apnea')) active = '/apnea';
    else if (p === '/chat') active = '/chat';
    else if (p === '/admin') active = '/admin';
    const rendered = [...topLevelItems, ...groups.flatMap(g => g.items)];
    return rendered.some(item => item.path === active) ? active : '/feed';
  };

  const activePath = getActivePath();
  const activeGroup = groups.find(g => g.items.some(item => item.path === activePath));
  const allItems = [...topLevelItems, ...groups.flatMap(g => g.items)];
  const activeLabel = allItems.find(item => item.path === activePath)?.label ?? '';
  // Mobile/desktop tab value: a top-level item's path, or its group's label.
  const tabValue = activeGroup ? activeGroup.label : activePath;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (groups.some(g => g.label === newValue)) return; // handled by onClick opening the menu instead
    navigate(newValue);
  };

  const closeMenu = () => setMenuAnchor(null);
  const handleMenuItemClick = (path: string) => {
    closeMenu();
    navigate(path);
  };

  const renderDrawerItem = (item: NavItem, indent = false) => (
    <ListItemButton
      key={item.label}
      selected={activePath === item.path}
      onClick={() => navigate(item.path)}
      sx={{ borderRadius: 2, mb: 0.5, py: indent ? 1 : 1.25, pl: indent ? 3 : undefined }}
    >
      <ListItemIcon sx={{ minWidth: indent ? 36 : 40 }}>{item.icon}</ListItemIcon>
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{ fontWeight: activePath === item.path ? 700 : 500 }}
      />
    </ListItemButton>
  );

  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
            px: 0.5,
            py: 0.75,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.1, color: 'text.secondary' }}>
              Navigation
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {activeLabel}
            </Typography>
          </Box>
          <IconButton
            aria-label="Open navigation menu"
            onClick={() => setIsDrawerOpen(true)}
            size="large"
            sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <Drawer
          anchor="left"
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          PaperProps={{ sx: { width: 'min(82vw, 320px)', pt: 2 } }}
        >
          <Box
            sx={{
              px: 2,
              pb: 1.75,
              mb: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
              background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.1)} 0%, transparent 100%)`,
            }}
          >
            <BrandLogo size={36} sx={{ color: 'text.primary' }} />
          </Box>
          <List sx={{ px: 1 }}>
            {renderDrawerItem(FEED_ITEM)}
            {groups.map((group) => (
              <React.Fragment key={group.label}>
                <ListSubheader sx={{ lineHeight: 2.2, bgcolor: 'transparent' }}>{group.label}</ListSubheader>
                {group.items.map((item) => renderDrawerItem(item, true))}
              </React.Fragment>
            ))}
            {topLevelItems.slice(1).map((item) => renderDrawerItem(item))}
          </List>
        </Drawer>
      </>
    );
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="standard"
        centered
        sx={{
          minHeight: 48,
          '& .MuiTab-root': {
            minHeight: 48,
            minWidth: 0,
            px: 1.5,
            py: 1.25,
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.2,
            textTransform: 'none',
          },
          '& .MuiTab-iconWrapper': { marginBottom: 0, marginRight: 1 },
        }}
      >
        <Tab value={FEED_ITEM.path} icon={FEED_ITEM.icon} label={FEED_ITEM.label} iconPosition="start" />

        {groups.map((group) => (
          <Tab
            key={group.label}
            value={group.label}
            icon={group.icon}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {group.label}
                <ArrowDropDown fontSize="small" sx={{ ml: 0.25 }} />
              </Box>
            }
            onClick={(event: React.MouseEvent<HTMLElement>) => setMenuAnchor({ label: group.label, el: event.currentTarget })}
            aria-haspopup="menu"
            aria-expanded={menuAnchor?.label === group.label}
          />
        ))}

        {topLevelItems.slice(1).map((item) => (
          <Tab key={item.label} value={item.path} icon={item.icon} label={item.label} iconPosition="start" />
        ))}
      </Tabs>

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {(groups.find(g => g.label === menuAnchor?.label)?.items ?? []).map((item) => (
          <MenuItem
            key={item.label}
            selected={activePath === item.path}
            onClick={() => handleMenuItemClick(item.path)}
            sx={{ gap: 1.25, minWidth: 180 }}
          >
            <ListItemIcon sx={{ minWidth: 'auto' }}>{item.icon}</ListItemIcon>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default AppNavigation;
